import { ImportProductResponseModel } from "./../Helpers/Interfaces/ImportProductResponse";
import { createApi } from "@reduxjs/toolkit/query/react";
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";
import ApiPath from "./ConstantApi/index";
import { SalesReportsModel } from "./../Helpers/Interfaces/SalesReports";
import { PaymentReportsModel } from "./../Helpers/Interfaces/PaymentReports";

export interface SupplierData {
  name: string;
  email: string;
  phone: string;
  address: string;
  isActive: true | false;
  logo?: File | null;
  _id?: string;
}

export interface SupplierResponse {
  _id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  isActive: true | false;
  logo?: string;
}

export interface SupplierListResponse {
  data: SupplierResponse[];
  totalRecords: number;
  totalPages: number;
  currentPage: number;
}

export interface SupplierListParams {
  page: number;
  pageSize: number;
}

export interface UpdateSupplierRequest {
  id: string;
  data: FormData;
}

export const ReportsService = createApi({
  reducerPath: "ReportsApi",
  baseQuery: RtkBaseQueryWithReauth(process.env.REACT_APP_BASE_URL || ""),
  tagTypes: ["Reports"],
  endpoints: (builder) => ({
    // getSupplierList: builder.query<SupplierListResponse, SupplierListParams>({
    //   query: (params) => ({
    //     url: `${ApiPath.getSupplierList}?page=${params.page}&pageSize=${params.pageSize}`,
    //     method: "GET",
    //   }),
    //   providesTags: ["Reports"],
    // }),

    // addAndUpdateSupplier: builder.mutation<SupplierResponse, FormData | UpdateSupplierRequest>({
    //   query: (payload) => {
    //     if ('id' in payload) {
    //       return {
    //         url: `${ApiPath.updateSupplier}/${payload.id}`,
    //         method: 'PUT',
    //         body: payload.data,
    //       };
    //     }
    //     return {
    //       url: ApiPath.createSupplier,
    //       method: 'POST',
    //       body: payload,
    //     };
    //   },
    //   invalidatesTags: ["Reports"],
    // }),

    // deleteSupplier: builder.mutation<void, string>({
    //   query: (id) => ({
    //     url: `${ApiPath.deleteSupplier}/${id}`,
    //     method: "DELETE",
    //   }),
    //   invalidatesTags: ["Reports"],
    // }),

    getSalesReport: builder.mutation<SalesReportsModel, {}>({
      query: (reqBody) => ({
        url: `${ApiPath.getSalesReport}`,
        method: "POST",
        body: reqBody,
      }),
    }),

    getProductReport: builder.mutation<
      ImportProductResponseModel,
      {
        page?: number;
        limit?: number;
        pageSize?: number;
        filters?: { key: string; value: any }[];
      }
    >({
      query: ({ page = 1, limit, pageSize, filters = [] }) => {
        const finalLimit = limit ?? pageSize ?? 10;
        const body = {
          page,
          pageSize: finalLimit,
          filters,
        };
        return {
          url: ApiPath.getProductReport,
          method: "POST",
          body,
        };
      },
    }),

    getPaymentStatus: builder.mutation<PaymentReportsModel, {}>({
      query: (reqBody) => ({
        url: ApiPath.paymentList,
        method: "POST",
        body: reqBody,
      }),
    }),

    getReportLogs: builder.mutation<SalesReportsModel, {}>({
      query: (reqBody) => ({
        url: ApiPath.reportLogs,
        method: "POST",
        body: reqBody,
      }),
    }),

    /*
     * exportPurchase and exportPayments were here.
     *
     * Both posted to an export endpoint of their own, separate from the one
     * that filled the table, and both had drifted from the screen they were
     * supposed to serve -- see exportReport below. Replaced by it, and
     * removed rather than left in place: an unused export mutation is what a
     * future screen picks up by name and inherits the same problem from.
     */

    /*
     * getCustomerReport was here.
     *
     * Its screen could never be reached -- two routes shared the same path
     * and the Customers tab won -- and the four figures it added are on
     * that tab now, from the customers endpoint itself. Removed rather
     * than left unused: an orphan mutation is what the next screen picks
     * up by name.
     */

    /**
     * The products and denominations the report filter panels offer.
     *
     * A query, not a mutation, so RTK Query caches it: every report screen
     * asks for the same list and only the first one goes to the network.
     *
     * Denominations come back nested inside their product, in the one
     * response. The two dropdowns are dependent -- the product decides what
     * the denomination list may hold -- and fetching denominations on
     * selection would put a round trip between the two clicks, which on a
     * filter panel reads as the second dropdown being broken.
     */
    getReportFilterOptions: builder.query<
      {
        status: string;
        data: {
          productId: string;
          productName: string;
          productType: string;
          denominations: {
            sku: string;
            denomination: number | null;
            label: string;
          }[];
        }[];
      },
      void
    >({
      query: () => ({
        url: ApiPath.reportFilterOptions,
        method: "GET",
      }),
    }),

    /**
     * Export a report table, using the request the table itself was filled
     * with.
     *
     * The caller passes the SAME body it just sent to list the rows, and this
     * adds `export: true`. The server answers that on the list endpoint by
     * running the same filter query without pagination and streaming a
     * spreadsheet, so the file cannot be a different set of rows from the
     * screen -- there is only one definition of the query, on both sides.
     *
     * That is the point. The exports this replaces each had their own
     * endpoint and their own filter parsing, and all of them had drifted:
     * Sales, Product and Pin Logs exported purchase orders, which none of
     * those screens shows, and Payments matched a misspelled filter key.
     */
    exportReport: builder.mutation<Blob, { url: string; body: any }>({
      async queryFn({ url, body }, _queryApi, _extraOptions, fetchWithBQ) {
        const result = await fetchWithBQ({
          url,
          method: "POST",
          body: { ...body, export: true },
          responseHandler: (response: { blob: () => any }) => response.blob(),
        });

        // A non-2xx still carries a body worth showing -- "no rows matched",
        // "narrow the date range". Handed back as a Blob either way and read
        // by downloadReport, which is the only place that knows whether what
        // arrived is a spreadsheet.
        if (result.error) {
          const data = (result.error as any)?.data;
          if (data instanceof Blob) return { data };
          return { error: result.error };
        }
        return { data: result.data as Blob };
      },
    }),

    getPinLogs: builder.mutation<
      any,
      {
        startDate?: string;
        endDate?: string;
        batch?: string;
        orderNumber?: string;
        action?: string;
        userId?: string;
        page?: number;
        pageSize?: number;
        filters?: { key: string; value: any }[];
      }
    >({
      query: ({
        startDate,
        endDate,
        batch,
        orderNumber,
        action,
        userId,
        page = 1,
        pageSize = 10,
        filters = [],
      }) => {
        const body: Record<string, any> = {
          // Prefer filters array when provided
          ...(Array.isArray(filters) && filters.length > 0 ? { filters } : {}),
          // Keep backward compatibility with individual keys
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
          ...(batch && { batch }),
          ...(orderNumber && { orderNumber }),
          ...(action && { action }),
          ...(userId && { userId }),
          page,
          pageSize,
        };

        return {
          url: ApiPath.getPinLogs,
          method: "POST",
          body,
        };
      },
    }),

    /**
     * The values the API Transactions filter panel can offer, read from the
     * data rather than hard-coded -- so it never lists a supplier with no rows
     * behind it, and never misses one added after this screen was written.
     */
    getApiTransactionFilterOptions: builder.query<any, void>({
      query: () => ({
        url: `${ApiPath.getApiTransactionFilterOptions}`,
        method: "GET",
      }),
    }),

    /**
     * Supplier reconciliation export.
     *
     * Returns the file on the request rather than queueing a job. The existing
     * async export mutations post a request and promise a notification, and
     * the six rows that produced have an empty file path and no file behind
     * them, which is why the download button beside this one is commented out.
     *
     * `format=json` is offered for a quick on-screen count before committing
     * to a download; everything else comes back as CSV.
     */
    exportSupplierReconciliation: builder.mutation<
      Blob,
      Record<string, string | boolean | undefined>
    >({
      async queryFn(args, _queryApi, _extraOptions, fetchWithBQ) {
        const qs = Object.entries(args)
          .filter(([, v]) => v !== undefined && v !== "" && v !== false)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join("&");

        const result = await fetchWithBQ({
          url: `${ApiPath.supplierReconciliationExport}?${qs}`,
          method: "GET",
          responseHandler: (response: { blob: () => any }) => response.blob(),
        });

        if (result.error) return { error: result.error };
        return { data: result.data as Blob };
      },
    }),

    getRabbitMQLogs: builder.mutation<
      any,
      { orderNumber: string; batch?: string; orderItemId?: string }
    >({
      /*
       * `orderItemId` scopes the modal to the line that was clicked.
       *
       * `batch` cannot: a FlixBus booking has no serial, so nothing was sent,
       * so nothing was narrowed and the modal showed every call on the order.
       * Opening the bus ticket on ORDE-352 listed the PJ Telesoft purchase
       * belonging to the other item.
       *
       * batch is still sent when present so older callers keep working; the
       * API prefers orderItemId when it gets both.
       */
      query: ({ orderNumber, batch, orderItemId }) => {
        const params = new URLSearchParams();
        if (orderItemId) params.set("orderItemId", String(orderItemId));
        if (batch) params.set("batchNo", String(batch));
        const qs = params.toString();
        return {
          url: `${ApiPath.getRabbitMQLogs}${orderNumber}/modal${qs ? `?${qs}` : ""}`,
          method: "GET",
        };
      },
    }),
  }),
});

export const {
  // useGetSupplierListQuery,
  // useLazyGetSupplierListQuery,
  // useAddAndUpdateSupplierMutation,
  // useDeleteSupplierMutation,

  useGetProductReportMutation,
  useGetSalesReportMutation,
  useGetPaymentStatusMutation,
  useExportReportMutation,
  useGetReportFilterOptionsQuery,
  useGetReportLogsMutation,
  useGetPinLogsMutation,
  useGetRabbitMQLogsMutation,
  useGetApiTransactionFilterOptionsQuery,
  useExportSupplierReconciliationMutation,
} = ReportsService;
